import { useState } from "react";
import { getWorkspaceTimeZone, getTimeZoneAbbreviation } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, History, Search, ArrowRight, X } from "lucide-react";
import type { InvoiceChangeLog } from "@shared/schema";

interface ChangeLogResponse {
  rows: InvoiceChangeLog[];
  total: number;
}

function formatTimestamp(value: string | Date | null): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    timeZone: getWorkspaceTimeZone(),
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function DiffValue({ value, tone }: { value: string | null; tone: "old" | "new" }) {
  const empty = value === null || value === "";
  return (
    <span
      className={
        empty
          ? "italic text-muted-foreground"
          : tone === "old"
            ? "text-destructive line-through decoration-destructive/40"
            : "text-emerald-600 dark:text-emerald-400 font-medium"
      }
    >
      {empty ? "(empty)" : value}
    </span>
  );
}

export function InvoiceChangeLogCard() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useQuery<ChangeLogResponse>({
    queryKey: ["/api/invoice-change-logs", search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      params.set("limit", "200");
      const res = await fetch(`/api/invoice-change-logs?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load invoice change logs");
      return res.json();
    },
  });

  const rows = data?.rows ?? [];

  const submitSearch = () => setSearch(searchInput.trim());
  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
  };

  return (
    <Card data-testid="card-invoice-change-log">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" /> Invoice Logs
        </CardTitle>
        <CardDescription>
          A field-level audit trail of changes to your invoices — who changed what, when, and the
          old → new values. History is shown most recent first. Times are in {getTimeZoneAbbreviation()}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              placeholder="Search by invoice #, field, value, or person…"
              className="pl-8"
              data-testid="input-invoice-log-search"
            />
          </div>
          <Button onClick={submitSearch} variant="secondary" data-testid="button-invoice-log-search">
            Search
          </Button>
          {search && (
            <Button onClick={clearSearch} variant="ghost" size="icon" data-testid="button-invoice-log-clear">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="status-invoice-log-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading change history…
          </div>
        ) : isError ? (
          <div className="py-12 text-center text-destructive" data-testid="status-invoice-log-error">
            Failed to load invoice change logs.
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground" data-testid="status-invoice-log-empty">
            {search
              ? "No changes match your search."
              : "No invoice changes have been recorded yet."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="list-invoice-change-log">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                data-testid={`row-invoice-log-${row.id}`}
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {row.invoiceNumber && (
                      <Badge variant="outline" data-testid={`badge-invoice-number-${row.id}`}>
                        #{row.invoiceNumber}
                      </Badge>
                    )}
                    <span className="font-medium" data-testid={`text-field-label-${row.id}`}>
                      {row.fieldLabel || row.fieldName}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm break-words">
                    <DiffValue value={row.oldValue} tone="old" />
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <DiffValue value={row.newValue} tone="new" />
                  </div>
                </div>
                <div className="shrink-0 text-right text-xs text-muted-foreground">
                  <div data-testid={`text-changed-by-${row.id}`}>{row.changedByName || "Unknown"}</div>
                  <div data-testid={`text-changed-at-${row.id}`}>{formatTimestamp(row.changedAt)}</div>
                </div>
              </div>
            ))}
            {data && data.total > rows.length && (
              <p className="pt-2 text-center text-xs text-muted-foreground">
                Showing {rows.length} of {data.total} entries. Refine your search to narrow results.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
